import { useState } from 'react'

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { Box, Container } from '@mui/material'

import { postSupportSearchResponse } from 'services/completions.service'

import SynthesisResponse from 'pages/SubmittedIdeas/components/SynthesisResponse/SynthesisResponse'

import SubPageHeader from 'components/SubPageHeader'

import { styles } from './Support.styles'
import SupportAccordion from './components/SupportAccordion'
import { FAQCategories, FAQCategoryDetails } from './constants/FAQ'

export const buildPageContent = (): string[] => {
	return Object.values(FAQCategoryDetails)
		.flat()
		.map((item) => `${item.title}: ${item.content}`)
}

export type HandleAskAIParams = {
	setAIResponse: React.Dispatch<React.SetStateAction<string | null>>
	setLoading: React.Dispatch<React.SetStateAction<boolean>>
	userQuery: string
}

export const handleAskAI = async ({
	userQuery,
	setLoading,
	setAIResponse,
}: HandleAskAIParams) => {
	setLoading(true)
	setAIResponse(null)

	const pageContent = buildPageContent()

	try {
		const response = await postSupportSearchResponse({
			userQuery,
			pageContent,
		})

		setAIResponse(response.answer || JSON.stringify(response))
	} catch {
		setAIResponse('Failed to get response. Please try again.')
	} finally {
		setLoading(false)
	}
}

export const Support = () => {
	const [activeFAQDropdown, setActiveFAQDropdown] = useState<number>(-1)
	const [loading, setLoading] = useState(false)
	const [aiResponse, setAIResponse] = useState<string | null>(null)
	const [userQuery, setUserQuery] = useState('')

	const clearResponse = () => setAIResponse(null)

	const isDisabled = !userQuery || loading

	return (
		<Box>
			<SubPageHeader
				title="Support Resources"
				subtitle="How to get the most out of Brainstorm to maximize your team's innovative potential"
				backgroundGradient="linear-gradient(90deg, #9DCDF2 0%, #BDDEF6 100%)"
			/>
			<Container maxWidth={false} sx={styles.container}>
				{/* Commented out search bar to be implemented in the future */}
				<Box sx={styles.headerTextBox}>
					<span style={styles.headerTextSpan}>
						Get a quick answer to your question by asking GenAI:
					</span>
				</Box>
				{/* Search Bar */}
				<Box sx={styles.searchBarContainer}>
					<Box
						component="input"
						type="text"
						placeholder="Ask AI a question about the support content ..."
						value={userQuery}
						onChange={(e) => setUserQuery(e.target.value)}
						sx={styles.searchInput(!!userQuery)}
						data-testid="ask-ai-input"
					/>
					{/* Ask AI Button */}
					<Box
						component="button"
						onClick={() =>
							handleAskAI({
								userQuery,
								setLoading,
								setAIResponse,
							})
						}
						disabled={isDisabled}
						sx={
							userQuery
								? styles.askButtonActive
								: styles.askButtonDisabled
						}
						data-testid="ask-ai-button"
					>
						<span style={styles.askButtonIconSpan}>
							<AutoAwesomeIcon />
						</span>
						{loading ? 'Thinking...' : 'ASK AI'}
					</Box>
				</Box>
				{/* AI Response */}
				{aiResponse && (
					<Box sx={styles.aiResponseContainer}>
						<SynthesisResponse
							data-testid="ai-response"
							contentText={aiResponse}
							clearResponse={clearResponse}
						/>
					</Box>
				)}
				{/* Help Text */}
				<Box sx={styles.helpText} data-testid="support-message">
					<span style={styles.helpTextSpan}>
						Need help from our team? Reach out anytime and
						we&apos;ll be in touch within 24 hours on business days.
					</span>
				</Box>

				<Box>
					<Box
						component="a"
						href="https://go.slalom.com/brainstorm-support"
						target="_blank"
						rel="noopener noreferrer"
						sx={styles.requestButton}
						data-testid="live-support-button"
					>
						REQUEST LIVE SUPPORT
					</Box>
				</Box>
				<Box>
					{/* Accordion Dropdown */}
					<Box sx={styles.accordionContainer}>
						{Object.values(FAQCategories).map((category, index) => (
							<Box
								key={category}
								data-testid={`faq-category-${category}`}
							>
								<SupportAccordion
									dropdownId={index}
									activeFAQDropdown={activeFAQDropdown}
									setActiveFAQDropdown={setActiveFAQDropdown}
									category={category}
								/>
							</Box>
						))}
					</Box>
				</Box>
			</Container>
		</Box>
	)
}

export default Support
